import { useEffect, useMemo } from 'react';
import { InlineLoading } from '@carbon/react';
import { useAtom } from 'jotai';
import { Layer, Source, useMap } from 'react-map-gl';

import { mapQueryFittedKeyAtom } from './atoms';
import { useMapZoom, useSourceLoading } from './hooks';
import { TRACT_SOURCE_ID } from './tract-layer';
import type { BoundingBox, MapQueryFit } from './types';
import { EXTENT_COLOR, insetRectangle } from './utils';
import styles from './map-query-source.module.scss';

// The outline sits this far inside the extent, so it shows inside the viewport.
const EXTENT_INSET = 0.02;

/**
 * "Zoom in to see tracts" while the map draws none: below the tracts source's
 * minzoom (county scale) with every Tract (or every one in view) a match.
 * Narrowed matches draw at every zoom. Search, counts and the table work at
 * every zoom. Says nothing until the source has loaded. The live region stays
 * mounted so a screen reader announces the note as it appears.
 *
 * @param {object} props - Whether the results are narrowed, and the minzoom
 * @param {boolean} props.narrowed - An entity, a location or a drawn polygon is set
 * @param {number | null} props.tractMinZoom - The tracts source's minzoom; null before it loads
 * @return {JSX.Element} The live region, holding the note while nothing is drawn
 */
export function ZoomInNote({
  narrowed,
  tractMinZoom,
}: {
  narrowed: boolean;
  tractMinZoom: number | null;
}) {
  const zoom = useMapZoom();
  const tooFarOut =
    !narrowed && tractMinZoom !== null && zoom !== null && zoom < tractMinZoom;
  return (
    <div className={styles.zoomNoteRegion} aria-live="polite">
      {tooFarOut && (
        <span className={styles.zoomNote}>Zoom in to see tracts</span>
      )}
    </div>
  );
}

/**
 * "Loading tracts…" on the map while the search runs or the tract tiles load.
 *
 * @param {object} props - Whether the search runs
 * @param {boolean} props.searching - True from a change of filters to its answer
 * @return {JSX.Element | null} The note, while either loads
 */
export function MapLoadingNote({ searching }: { searching: boolean }) {
  const tilesLoading = useSourceLoading(TRACT_SOURCE_ID);
  if (!searching && !tilesLoading) return null;
  return (
    <InlineLoading
      className={styles.mapLoading}
      description="Loading tracts…"
    />
  );
}

/**
 * A dashed outline of the visible extent the search is using.
 *
 * @param {object} props - The extent
 * @param {BoundingBox} props.extent - The searched visible extent
 * @return {JSX.Element} The outline's source and layers
 */
export function ExtentOutline({ extent }: { extent: BoundingBox }) {
  const data = useMemo(() => insetRectangle(extent, EXTENT_INSET), [extent]);
  return (
    <Source id="package-map-query-extent" type="geojson" data={data}>
      <Layer
        id="package-map-query-extent-fill"
        type="fill"
        paint={{ 'fill-color': EXTENT_COLOR, 'fill-opacity': 0.04 }}
      />
      <Layer
        id="package-map-query-extent-line"
        type="line"
        paint={{
          'line-color': EXTENT_COLOR,
          'line-width': 1.5,
          'line-dasharray': [4, 3],
        }}
      />
    </Source>
  );
}

/**
 * Fits the map to the results once per change of filters, as Map Query's map
 * does (pages/map-query/map-section/fit-bounds-on-search.tsx): at most zoom 16,
 * over a second. Padded 40, as the wizard's other map is, not Map Query's 100,
 * which leaves 56 px of this map's 256 at a laptop window. To all the results,
 * however far apart, as the package pages fit theirs; they draw at every zoom.
 * The filters it last fitted to outlive the map, so the map reopened
 * on Back keeps where it was. A fit is the map's own move, so it changes no
 * visible extent.
 *
 * @param {object} props - The box to fit, null for none
 * @param {MapQueryFit | null} props.fit - The results box and the filters it answers
 * @return {null} Renders nothing
 */
export function FitToResults({ fit }: { fit: MapQueryFit | null }) {
  const { current: map } = useMap();
  const [fittedKey, setFittedKey] = useAtom(mapQueryFittedKeyAtom);
  useEffect(() => {
    if (!map || !fit || fit.key === fittedKey) return;
    setFittedKey(fit.key);
    const [west, south, east, north] = fit.bbox;
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 40, maxZoom: 16, duration: 1000 }
    );
  }, [map, fit, fittedKey, setFittedKey]);
  return null;
}
